import { useEffect, useState } from 'react';
import { Modal, Button } from '@symphenia/ui-components';
import { fieldLabel, fieldWrapper, inputBase } from '@/lib/form-styles';
import { createWorkSchedule } from '../api/payroll.api';
import { formatIsoDate, todayIso } from '../lib/dates';
import type { WorkSchedule } from '../types/payroll.types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Das Modell, das ab dem Stichtag abgelöst wird. */
  modell: WorkSchedule;
  /** Nach dem Anlegen öffnet die Seite das neue Modell. */
  onAngelegt: (schedule: WorkSchedule) => void;
}

/**
 * Wechsel des Arbeitszeitmodells ab einem Stichtag (AP-22 Teil 7, AP-36 Fall 2).
 *
 * Gefragt wird nur das Gültig-ab. Alles andere übernimmt das neue Modell vom alten und wird
 * danach auf seiner Detailseite geändert — das alte bleibt für die Zeit davor stehen. Für den
 * Server ist das ein gewöhnliches Anlegen mit vollständigem Tages-Soll.
 */
export function WorkScheduleChangeDialog({ isOpen, onClose, modell, onAngelegt }: Props) {
  const [validFrom, setValidFrom] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setValidFrom(todayIso());
    setError(null);
  }, [isOpen]);

  const anlegen = async () => {
    if (!validFrom) return setError('Bitte ein Gültig-ab-Datum angeben.');
    if (validFrom <= modell.validFrom)
      return setError(`Der Wechsel muss nach dem ${formatIsoDate(modell.validFrom)} liegen.`);

    setSaving(true);
    setError(null);
    try {
      const angelegt = await createWorkSchedule({
        userId: modell.userId,
        validFrom,
        weeklyHours: modell.weeklyHours,
        employment: modell.employment,
        cycleWeeks: modell.cycleWeeks,
        ...(modell.cycleAnchor ? { cycleAnchor: modell.cycleAnchor } : {}),
        ...(modell.entryDate ? { entryDate: modell.entryDate } : {}),
        ...(modell.exitDate ? { exitDate: modell.exitDate } : {}),
        collectiveAgreementId: modell.collectiveAgreementId,
        hoursFrame: modell.hoursFrame,
        frameHours: modell.frameHours,
        frameOvertime: modell.frameOvertime,
        days: modell.days.map(({ week, weekday, targetHours }) => ({ week, weekday, targetHours })),
      });
      onAngelegt(angelegt);
    } catch (err) {
      const antwort = (err as { response?: { data?: { message?: string; code?: string } } })?.response?.data;
      setError(
        antwort?.message === 'payroll.workSchedules.duplicateValidFrom'
          ? 'Für diesen Mitarbeiter gibt es bereits ein Modell mit diesem Gültig-ab.'
          : antwort?.code === 'PAYROLL_COMPENSATION_CHANGE_MID_MONTH'
            ? 'Die Entlohnungsart kann nur zu einem Monatsersten wechseln — wähl als Gültig-ab den Ersten eines Monats.'
            : 'Anlegen fehlgeschlagen.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Wechsel des Arbeitszeitmodells"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={() => void anlegen()} disabled={saving}>
            {saving ? 'Anlegen…' : 'Anlegen'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>
        )}
        <div className={fieldWrapper}>
          <label className={fieldLabel} htmlFor="ws-change-validfrom">
            Gültig ab
          </label>
          <input
            id="ws-change-validfrom"
            type="date"
            className={inputBase}
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Das neue Modell übernimmt alle Werte des bisherigen; ändern kannst du sie danach im neuen
          Modell. Das Modell ab {formatIsoDate(modell.validFrom)} bleibt für die Zeit davor stehen.
        </p>
      </div>
    </Modal>
  );
}
